import React, { ComponentProps } from "react";
import { TextInputForm } from "./forms/text_input";
import { setDocTitle } from "./web_ui";

// pick a date util library
import * as luxon from "luxon";
import FadeIn from "../../../src/vendored/FadeIn";
import { RRule } from "rrule";

import classNames from "classnames";
import { Duration } from "luxon";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { common_format_utils } from "../../../common/common_format_utils";
import { common_utils } from "../../../common/common_utils";
import { Constants } from "../../../common/constants";
import {
	DiscordAuthToken,
	EventCreationProps,
	EventCreationState,
	EventCreationStateFields,
	GetEventDataRequest,
	ISendCreateEventMessage,
	ISendEditEventMessage,
	IWebConfirmationData,
	IWebEventCreateConfirmationData,
	Mentionable,
	PlaceholderFields,
	SeshTextChannelData,
	WebChannelTypes,
} from "../../../common/sesh_types";
import { time_format_utils } from "../../../common/time_format_utils";
import {
	guildSettingsStyle_getIndividualChannelSettingOverride,
	guildSettingsStyle_hasActiveChannelOverrideForSetting,
} from "../../../common/type_utils";
import {
	DashboardUtils,
	FADE_IN_ANIMATION_DURATION,
	LinkChainData,
	RevertDescriptor,
} from "./dashboard_utils";
import EventColorPicker from "./forms/event_color_picker";
import { ImageUploaderForm } from "./forms/image_uploader";
import { AddRoleToAttendeesEventForm } from "./forms/improved_components/add_role_to_attendees_event_form";
import { EventCreationAdvancedSelector } from "./forms/improved_components/advanced_event_creation_selector";
import { ChannelSelectFormWithTags } from "./forms/improved_components/channel_dropdown_with_tags";
import { DateFormWithDuration } from "./forms/improved_components/date_field_with_duration";
import {
	EventTemplateDropdown,
	EventTemplatePage,
	SaveCurrentSettingsButton,
} from "./forms/improved_components/event_templates/event_template_page";
import { EventThreadAccordian } from "./forms/improved_components/event_thread_form";
import { redirectToEventListFromCreatePage } from "./forms/improved_components/listings_form/event/event_list_base";
import { Note } from "./forms/improved_components/note";
import { RepeatFormComponent } from "./forms/improved_components/repeat_form_component";
import { ServerRemindersForm } from "./forms/improved_components/server_reminders/server_reminders_blurb";
import { SeshAccordianComponent } from "./forms/improved_components/sesh_accordian_component";
import { SignupOptionsBlurb } from "./forms/improved_components/signup_options/signup_option_blurb";
import DiscordNativeEventForm from "./forms/native_event_form";
import { RoleRestrictionsForm } from "./forms/role_restrictions";
import { TextAreaForm } from "./forms/text_area";
import { GuildSettingsFormStateKeys } from "./guild_settings/guild_settings";
import { PollCreationInputKeys } from "./poll_creation";
import {
	createInitialEventState,
	DefaultGuildSettingValues,
	EventSettingToGuildSetting,
	GuildDefaultsWithoutTimezone,
	GuildSettingToEventSetting,
	ModifiableGuildDefaultsKeys,
	ServerRemindersGroupedKeys,
} from "./utils/event_creation_utils";
import { LockRSVPsBefore } from "./forms/improved_components/lock_rsvps_before/lock_rsvps_before";

export type EventCreationInputKeys = EventCreationStateFields;

export const PremiumFeatures: Array<
	EventCreationInputKeys | PollCreationInputKeys | GuildSettingsFormStateKeys
> = ["rrule", "eligible_role_ids", "attendee_role_data", "is_ai_enabled"];

// Janky way to require all types in an ordered object
const FIELD_ORDER: Partial<Record<EventCreationInputKeys, undefined>> = {
	event_name: undefined,
	start_time: undefined,
	description: undefined,
	selected_channel: undefined,
};

export const PlaceholderNames = [
	"Apex Legends",
	"Minecraft",
	"Valorant 5 Stack",
];

export const PlaceholderDescriptions = [
	"Let's have fun :D",
	"First five to RSVP get dibs!",
	"Invite your friends!",
];

export const EventCreationContext = React.createContext<{
	state: EventCreation["state"];
	props: EventCreation["props"];
	setState: EventCreation["setState"];
}>(undefined);

export const PlaceholderTimes = [
	{
		start_time: "in 5 hours",
		duration: "1 hr 20 min",
	},
	{
		start_time: "Tomorrow at 5pm",
		duration: "1 hr 30 min",
	},
	{
		start_time: "8/21 at 2 pm",
		duration: "1 hr 40 min",
	},
];

export function IDsToMentionables(
	user_ids: string[],
	mentionables: Mentionable[]
) {
	if (!mentionables) {
		return [];
	}

	return mentionables.filter((val) => user_ids.includes(val.id));
}

export async function fetchEventData(
	auth_token: DiscordAuthToken,
	guild_id: string,
	message_id?: string
) {
	let request_body: GetEventDataRequest = {
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
		guild_id: guild_id,
		message_id: message_id,
	};

	return fetch(`/api/get_event_data`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: EventCreationProps) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
				return;
			}

			return value;
		})
	);
}

export type EventCreationPropsWithHistory = EventCreationProps &
	RouteComponentProps;

export class EventCreation extends React.Component<
	EventCreationPropsWithHistory,
	EventCreationState
> {
	private fields: { [key: string]: Function } = {};

	private modified_settings: {
		[key in keyof EventCreationProps["guild_defaults"]]?: true;
	} = {} as const;

	private pending_revert_state_updates: Partial<
		Record<ModifiableGuildDefaultsKeys, true>
	> = {};

	constructor(props) {
		super(props);

		if (this.props.template_data) {
			for (const key in this.props.template_data.json) {
				this.modified_settings[EventSettingToGuildSetting[key]] = true;
			}
		}

		this.handleSubmit = this.handleSubmit.bind(this);
		this.getGuildOrChannelSetting =
			this.getGuildOrChannelSetting.bind(this);
		this.getSelectedChannel.bind(this);

		this.state = this.createInitialState();

		this.populate_field_list("event_name", this.createTitleField);
		this.populate_field_list("start_time", this.createTimeField);
		this.populate_field_list("selected_channel", this.createChannelField);
		this.populate_field_list("rsvp_options", this.createRSVPOptionsField);
		this.populate_field_list("description", this.createDescriptionField);
		this.populate_field_list("thread_settings", this.createThreadField);
		this.populate_field_list("color", this.createColorField);
		this.populate_field_list("image_url", this.createImageField);
		this.populate_field_list(
			"hide_attendees",
			this.createHideEventAttendeesField
		);
		this.populate_field_list(
			"native_event_data",
			this.createDiscordNativeEventMirrorField
		);
		this.populate_field_list(
			"eligible_role_ids",
			this.createRoleRestrictionsField
		);
		this.populate_field_list(
			"attendee_role_data",
			this.createAddRoleToAttendeesField
		);
		this.populate_field_list("rrule", this.createRepeatField);

		this.populate_field_list(
			"rsvp_lock_before_ms",
			this.createLockRSVPsBeforeField
		);

		this.populate_field_list(
			"server_reminders",
			this.createServerRemindersField
		);
	}

	createInitialState(force_new: boolean = false) {
		let placeholder_name =
			PlaceholderNames[
				Math.floor(Math.random() * PlaceholderNames.length)
			];
		let placeholder_description =
			PlaceholderDescriptions[
				Math.floor(Math.random() * PlaceholderDescriptions.length)
			];
		let placeholder_time_fields =
			PlaceholderTimes[
				Math.floor(Math.random() * PlaceholderTimes.length)
			];

		let placeholder_fields: PlaceholderFields = {
			event_name: placeholder_name,
			description: placeholder_description,
			start_time: placeholder_time_fields.start_time,
			duration: placeholder_time_fields.duration,
		};

		for (let key in placeholder_fields) {
			placeholder_fields[key] = "ex: " + placeholder_fields[key];
		}

		let user_timezone = luxon.DateTime.local().zone.name;

		setDocTitle(
			this.props.is_edit ? "Editing event" : "Creating event",
			this.props.guild_name
		);

		if (!force_new && this.props.is_edit) {
			if (!this.props.edit_data) {
				DashboardUtils.ShowDefaultErrorAlert("No edit data found.");
				return;
			}
		}

		const editable_state = createInitialEventState(this, {
			user_timezone,
		});

		const base_state: Omit<
			EventCreationState,
			keyof ReturnType<typeof createInitialEventState>
		> = {
			can_make_recurring: this.props.can_make_recurring,
			guild_data_for_this_guild: this.props.all_guild_data.find(
				(val) => val.id === this.props.guild_id
			),
			guild_timezone: this.props.guild_timezone,
			header_text: this.props.is_edit
				? this.getEditHeaderText(this.props.edit_data.event_name)
				: this.getCreateHeaderText(),
			inform_users_of_edit: true,
			is_confirming: false,
			is_edit: this.props.is_edit,
			loading_roles_and_channels: !Boolean(this.props.roles),
			use_24h_clock: this.props.use_24h_clock,
			confirmation_data: null,
			custom_emojis: this.props.custom_emojis,
			user_presets: this.props.user_presets,
			placeholder_fields: placeholder_fields,
			original_start_time: editable_state.start_time,
			user_timezone: user_timezone,
			current_page: "main",
		};

		const state: EventCreationState = {
			...editable_state,
			...base_state,
		};

		return state;
	}

	getUpdatedChannelData(): {
		selected_channel: SeshTextChannelData;
	} {
		const roles = this.props.roles;

		if (!roles) {
			return {
				selected_channel: null,
			};
		}

		const all_channels = this.props.is_edit
			? this.props.all_text_channels
			: this.props.all_text_channels.filter((val) => val.has_write);

		const query = new URLSearchParams(this.props.location.search);
		const channel_id_from_param = query.get("channel_id");

		let selected_channel_id: string;

		if (this.props.is_edit) {
			selected_channel_id = this.props.edit_data.sent_channel_data.id;
		} else if (this.props.template_data) {
			// If there's a channel id in param, prioritize that
			selected_channel_id = channel_id_from_param
				? channel_id_from_param
				: this.props.template_data.json.sent_channel_data.id;
		} else if (this.props.can_select_channel) {
			selected_channel_id = Boolean(channel_id_from_param)
				? channel_id_from_param
				: this.props.event_channel_id;
		} else {
			selected_channel_id = this.props.event_channel_id;
		}

		let selected_channel = all_channels.find(
			(val) => val.id === selected_channel_id
		);

		if (this.props.is_edit) {
			if (!selected_channel || !selected_channel.has_write) {
				let error_text: string;
				if (!selected_channel) {
					error_text = `Original event channel (id: ${this.props.edit_data.sent_channel_data.id}) not found. Please ensure both you and sesh have read/write/embed links permissions in the original channel of this event.`;
				} else {
					error_text = `Invalid permissions for channel #${selected_channel.name}.  Please ensure both you and sesh have read/write/embed links permissions in the channel.`;
				}

				DashboardUtils.ShowDefaultErrorAlert(error_text);

				this.props.history.push(
					common_utils.getGuildOptionsURL(this.props.guild_id)
				);
				return;
			}
		}

		if (!selected_channel) {
			selected_channel = all_channels[0];
		}

		if (!selected_channel) {
			DashboardUtils.ShowDefaultErrorAlert(
				"No valid channels found. Please ensure both you and sesh have read/write/embed links permissions in atleast one channel in the server."
			);

			this.props.history.push(
				common_utils.getGuildOptionsURL(this.props.guild_id)
			);
			return;
		}

		if (!this.props.is_edit) {
			query.set("channel_id", selected_channel.id);
			this.props.history.replace(`create?${query}`);
		}

		return {
			selected_channel,
		};
	}

	componentDidMount() {
		if (this.props.is_edit) {
			if (this.state.start_time < luxon.DateTime.utc()) {
				DashboardUtils.ShowDefaultErrorAlert(
					"This event has already started! Unable to edit."
				);
				this.props.history.replace(
					common_utils.getEventCreateURL(this.props.guild_id)
				);
				return;
			}
		}
	}

	componentWillUnmount() {
		if (this.props.on_unmount) {
			this.props.on_unmount();
		}
	}

	getSelectedChannel() {
		return this.props.all_text_channels?.find(
			(val) => val.id === this.state.selected_channel?.id
		);
	}

	getModifiedSettingsKey(key: keyof GuildDefaultsWithoutTimezone) {
		return this.modified_settings[key];
	}

	updateModifiedSettingsKey(
		key: keyof GuildDefaultsWithoutTimezone,
		val: boolean
	) {
		if (!val) {
			delete this.modified_settings[key];
			return;
		}

		if (
			ServerRemindersGroupedKeys.includes(
				key as keyof typeof EventSettingToGuildSetting
			)
		) {
			ServerRemindersGroupedKeys.forEach(
				(val) => (this.modified_settings[val] = true)
			);
			return;
		}

		this.modified_settings[key] = true;
	}

	componentDidUpdate(
		prevProps: EventCreationProps & RouteComponentProps,
		prevState: EventCreationState
	) {
		const roles_and_channels_updated =
			prevProps.roles == null && this.props.roles != null;

		if (prevState.selected_channel?.id == this.state.selected_channel?.id) {
			// Only update this if the state change was *not* caused by channel switching

			for (const key of Object.keys(EventSettingToGuildSetting)) {
				if (this.pending_revert_state_updates[key]) {
					delete this.pending_revert_state_updates[key];
					continue;
				}

				if (
					common_utils.areObjectsEqual(
						prevState[key],
						this.state[key]
					)
				) {
					continue;
				}

				// Used to keep track of what settings were modified.
				// If a setting was *not* modified, then we want to update it to the default on channel switch
				this.updateModifiedSettingsKey(
					EventSettingToGuildSetting[
						key as keyof typeof EventSettingToGuildSetting
					],
					true
				);
			}
		}

		if (roles_and_channels_updated) {
			const roles_and_channels_data = this.getUpdatedChannelData();

			this.setState({
				selected_channel: roles_and_channels_data?.selected_channel
					? {
							id: roles_and_channels_data.selected_channel.id,
							forum_tag_ids: [],
					  }
					: undefined,
				loading_roles_and_channels: false,
			});
		}

		if (!this.props.is_edit) {
			const channel_changed =
				prevState.selected_channel?.id !=
				this.state.selected_channel?.id;

			if (channel_changed) {
				if (this.state.selected_channel) {
					const query = new URLSearchParams(
						this.props.location.search
					);
					query.set("channel_id", this.state.selected_channel.id);
					this.props.history.replace(`create?${query}`);
				}
			}
		}
	}

	populate_field_list(key: EventCreationInputKeys, value: Function) {
		value = value.bind(this);
		this.fields[key.toString()] = value;
	}

	getCalculatedEndTimeIfSet() {
		if (!this.state.duration_ms || !this.state.start_time) {
			return;
		}

		return this.state.start_time.plus({
			millisecond: this.state.duration_ms,
		});
	}

	handleSubmit(event) {
		event.preventDefault();

		if (this.state.event_name.length === 0) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Event name is required!",
				undefined,
				() => this.scrollToElement("event_name")
			);
			return;
		}

		if (!this.state.start_time) {
			DashboardUtils.ShowDefaultErrorAlert(
				"A valid start time is required!",
				undefined,
				() => this.scrollToElement("start_time")
			);
			return;
		}

		if (this.state.start_time < luxon.DateTime.utc()) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Can't make an event with a start time in the past!",
				undefined,
				() => this.scrollToElement("start_time")
			);
			return;
		}

		const calculated_end_time = this.getCalculatedEndTimeIfSet();

		if (
			this.getCalculatedEndTimeIfSet() &&
			this.state.start_time > calculated_end_time
		) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Start time must be earlier than end time!",
				undefined,
				() => this.scrollToElement("start_time")
			);
			return;
		}

		if (this.state.rsvp_options) {
			let dupe = common_format_utils.get_duplicate_emojis_in_preset(
				this.state.rsvp_options
			);
			if (dupe) {
				DashboardUtils.ShowDefaultErrorAlert(
					`Duplicate custom emojis [${dupe}]! Can not use the same emoji for multiple options.`,
					undefined,
					() => this.scrollToElement("rsvp_options")
				);
				return;
			}
		}

		const selected_channel = this.getSelectedChannel();
		if (
			selected_channel.type === "Forum" &&
			selected_channel.tag_settings.required &&
			this.state.selected_channel.forum_tag_ids.length === 0
		) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Must have at least one tag selected for this channel",
				undefined,
				() => this.scrollToElement("selected_channel")
			);
			return;
		}

		let rrule_str;
		if (this.state.rrule) {
			let first_two_repeat_times = this.state.rrule.all((d, l) => {
				return l < 2;
			});

			if (first_two_repeat_times.length >= 2) {
				let first_repeat_time = luxon.DateTime.fromJSDate(
					first_two_repeat_times[0]
				);

				let second_repeat_time = luxon.DateTime.fromJSDate(
					first_two_repeat_times[1]
				);

				let repeat_delay = second_repeat_time.diff(first_repeat_time);
				let event_duration;
				if (this.state.duration_ms) {
					event_duration = Duration.fromMillis(
						this.state.duration_ms
					);
				} else {
					event_duration = luxon.Duration.fromObject(
						Constants.DEFAULT_EVENT_DURATION
					);
				}
				if (repeat_delay.as("minutes") < event_duration.as("minutes")) {
					DashboardUtils.ShowDefaultErrorAlert(
						"Can not make an event repeat faster than the event's duration.\nSet the end time earlier to fix this.",
						undefined,
						() => this.scrollToElement("rrule")
					);
					return;
				}
			}

			rrule_str = RRule.optionsToString(
				time_format_utils.transformRRuleToTimezone(
					this.state.rrule.origOptions,
					this.state.user_timezone,
					this.state.start_time
				)
			);
		}

		const local_timezone = luxon.DateTime.local().zone.name;

		if (!this.state.is_edit) {
			this.setState({ is_confirming: true });

			const props = this.props;

			const is_attendee_role_data_overriding_server_default =
				!common_utils.areAttendeeRoleDatasEqual(
					this.getGuildOrChannelSetting("attendee_role_data"),
					this.state.attendee_role_data
				);

			const is_native_event_data_overriding_server_default =
				!common_utils.areObjectsEqual(
					this.getGuildOrChannelSetting("default_native_event_data"),
					this.state.native_event_data
				);

			const is_thread_settings_overriding_server_default =
				!common_utils.areObjectsEqual(
					this.getGuildOrChannelSetting(
						"default_event_thread_settings"
					),
					this.state.thread_settings
				);

			sendEventData({
				user_id: this.props.user_id,
				guild_id: this.props.guild_id,
				event_name: this.state.event_name,
				start_time_str: this.state.start_time.toISO(),
				end_time_str: this.getCalculatedEndTimeIfSet()?.toISO(),
				rrule_string: rrule_str,
				allow_maintain_repeat_rsvp:
					this.state.allow_maintain_repeat_rsvp,
				channel_id: this.state.selected_channel.id,
				event_description: this.state.description,
				rsvp_options: this.state.rsvp_options,
				color: this.state.color,
				mention_data: {
					create_role_mentions: this.state.role_mentions_on_create,
					create_user_mentions: this.state.user_mentions_on_create,
				},
				eligible_role_ids: this.state.eligible_role_ids,
				hide_attendees: this.state.hide_attendees,
				image_url: this.state.image_url,
				attendee_role_override_data: {
					is_overriding_server_default:
						is_attendee_role_data_overriding_server_default,
					value: this.state.attendee_role_data,
				},
				server_reminders: this.state.server_reminders,
				access_token: this.props.auth_token.access_token,
				forum_tag_ids: this.state.selected_channel.forum_tag_ids,

				native_event_override_data: {
					is_overriding_server_default:
						is_native_event_data_overriding_server_default,
					value: is_native_event_data_overriding_server_default
						? this.state.native_event_data
						: undefined,
				},
				thread_settings:
					this.getSelectedChannel().type !== "Thread"
						? {
								value: this.state.thread_settings,
								is_overriding_server_default:
									is_thread_settings_overriding_server_default,
						  }
						: undefined,
				event_timezone: this.state.event_timezone,
				token_type: this.props.auth_token.token_type,
				date_input_description: {
					input: this.state.date_input,
					timezone: local_timezone,
				},
				exdates: this.state.exdates?.map((val) => val.toISOString()),
				rdates: this.state.rdates?.map((val) => ({
					orig_date: new Date(val.orig_date).toISOString(),
					rdate: new Date(val.rdate).toISOString(),
				})),
				rsvp_lock_before_ms: this.state.rsvp_lock_before_ms,
			}).then((response) => {
				response
					.json()
					.then((val: IWebEventCreateConfirmationData) => {
						if (val.error) {
							DashboardUtils.ShowDefaultErrorAlert(
								common_utils.markdownToHtml(val.error)
							);
							this.setState({ is_confirming: false });
						} else {
							redirectToEventListFromCreatePage(
								this.props,
								val.message_id,
								this.state.start_time.toJSDate(),
								val.link
							);
						}
					})
					.catch((err) => {
						DashboardUtils.ShowDefaultErrorAlert(
							response.statusText
						);
						this.setState({ is_confirming: false });
					});
			});
		} else {
			let already_started =
				this.state.original_start_time.diffNow("milliseconds")
					.milliseconds < 0;
			if (already_started) {
				// TODO web refactor: fix this redirect
				DashboardUtils.ShowDefaultErrorAlert(
					"This event has already started. Unable to edit."
				);
				this.setState(this.createInitialState(true));
				return;
			}

			this.setState({ is_confirming: true });

			const props = this.props;

			sendEditData({
				user_id: this.props.user_id,
				guild_id: this.props.guild_id,
				event_name: this.state.event_name,
				start_time_str: this.state.start_time.toISO(),
				end_time_str: this.getCalculatedEndTimeIfSet()?.toISO(),
				rrule_string: rrule_str,
				allow_maintain_repeat_rsvp:
					this.state.allow_maintain_repeat_rsvp,
				message_id: this.props.edit_data.event_message_id,
				event_description: this.state.description,
				rsvp_options: this.state.rsvp_options,
				color: this.state.color,
				mention_data: {
					create_role_mentions: this.state.role_mentions_on_create,
					create_user_mentions: this.state.user_mentions_on_create,
				},
				eligible_role_ids: this.state.eligible_role_ids,
				channel_id: this.state.selected_channel?.id,
				server_reminders: this.state.server_reminders,
				forum_tag_ids: this.state.selected_channel?.forum_tag_ids,
				hide_attendees: this.state.hide_attendees,
				image_url: this.state.image_url,
				inform_users_of_edit: this.state.inform_users_of_edit,
				attendee_role_data: this.state.attendee_role_data,
				event_timezone: this.state.event_timezone,
				native_event_data: this.state.native_event_data,
				thread_settings: this.state.thread_settings,
				access_token: this.props.auth_token.access_token,
				token_type: this.props.auth_token.token_type,
				date_input_description: this.state.date_input
					? {
							input: this.state.date_input,
							timezone: local_timezone,
					  }
					: undefined,
				exdates: this.state.exdates?.map((val) => val.toISOString()),
				rdates: this.state.rdates?.map((val) => ({
					orig_date: new Date(val.orig_date).toISOString(),
					rdate: new Date(val.rdate).toISOString(),
				})),
				rsvp_lock_before_ms: this.state.rsvp_lock_before_ms,
			}).then((response) => {
				response
					.json()
					.then((val: IWebConfirmationData) => {
						if (val.error) {
							DashboardUtils.ShowDefaultErrorAlert(
								common_utils.markdownToHtml(val.error)
							);
							this.setState({ is_confirming: false });
						} else {
							redirectToEventListFromCreatePage(
								this.props,
								this.props.edit_data.event_message_id,
								this.state.start_time.toJSDate()
							);
						}
					})
					.catch((err) => {
						DashboardUtils.ShowDefaultErrorAlert(
							response.statusText
						);
						this.setState({ is_confirming: false });
					});
			});
		}
	}

	updateState<Key extends EventCreationInputKeys & keyof EventCreationState>(
		state_key: Key,
		value: EventCreationState[Key]
	) {
		let key = state_key as string;
		const new_state = {};
		new_state[key] = value;
		//this.state[key] = value;

		this.setState(new_state);
	}

	getEditHeaderText(event_name) {
		return (
			<span>
				{DashboardUtils.GetEventIcon()}
				Editing <i>{event_name}</i>
			</span>
		);
	}

	getCreateHeaderText() {
		return (
			<span>
				{DashboardUtils.GetEventIcon()}
				Creating Event
			</span>
		);
	}

	scrollToElement(id: EventCreationInputKeys) {
		document.getElementById(id).scrollIntoView({ behavior: "smooth" });
	}

	getCurrentGuildTimezone() {
		return this.getGuildOrChannelSetting("timezone");
	}

	createTitleField(force_disable: boolean = false) {
		return (
			<TextInputForm
				id="event_name"
				key="event_name"
				name="Event Title"
				placeholder={this.state.placeholder_fields.event_name}
				initial_value={this.state.event_name}
				updateFunc={(val) => this.updateState("event_name", val)}
				char_limit={Constants.EVENT_CHAR_LIMIT}
				info={"The title for the event"}
				disabled={force_disable}
			></TextInputForm>
		);
	}

	createTimeField(force_disable: boolean = false) {
		return (
			<DateFormWithDuration
				id="start_time"
				key="start_time"
				name="Start Time"
				placeholder={this.state.placeholder_fields.start_time}
				guild_zone={this.getCurrentGuildTimezone()}
				personal_zone={this.state.user_timezone}
				initial_value={this.state.start_time}
				use_24h_clock={this.state.use_24h_clock}
				duration_ms={this.state.duration_ms}
				display_text={"Event Time"}
				updateFunc={(val) => this.updateState("start_time", val)}
				onInputChange={(val) =>
					this.setState({
						date_input: val,
					})
				}
				info={"The start time for the event"}
				disabled={force_disable}
			/>
		);
	}

	createServerRemindersField(force_disable: boolean = false) {
		return (
			<ServerRemindersForm
				key={`server_reminders_${this.state.selected_channel?.id}`}
				is_disabled={force_disable}
				reminders={this.state.server_reminders}
				event_channel_id={this.state.selected_channel?.id}
				event_start_time={this.state.start_time?.toJSDate()}
				setReminders={(reminders) =>
					this.setState({
						server_reminders: reminders,
					})
				}
				initial_mentions={{
					create_role_mentions: this.state.role_mentions_on_create,
					create_user_mentions: this.state.user_mentions_on_create,
				}}
				setInitialMentions={(mentions) => {
					this.updateState(
						"role_mentions_on_create",
						mentions.create_role_mentions
					);
					this.updateState(
						"user_mentions_on_create",
						mentions.create_user_mentions
					);
				}}
				revert_descriptor={[
					this.getRevertDescriptor("default_event_server_reminders"),
					this.getRevertDescriptor(
						"default_role_mentions_on_event_create"
					),
					this.getRevertDescriptor(
						"default_user_mentions_on_event_create"
					),
				]}
			/>
		);
	}

	createChannelField(force_disable: boolean = false) {
		const channel_select_disabled =
			!this.props.can_select_channel || this.state.is_confirming;

		let channel_info_addenum: JSX.Element;
		if (!this.props.can_select_channel) {
			channel_info_addenum = (
				<i>. Disabled due to sesh server settings.</i>
			);
		}

		const channel_select_info = (
			<p>
				The channel where the event will get sent to
				{channel_info_addenum}
			</p>
		);

		const name = "Channel";

		if (this.state.loading_roles_and_channels) {
			return (
				<div key="selected_channel_load" className="field">
					{DashboardUtils.CreateHeaderAndInfoButton(
						name,
						channel_select_info
					)}
					{DashboardUtils.RenderLoadingSelect()}
				</div>
			);
		}

		let below_element: JSX.Element;
		if (
			this.props.is_edit &&
			this.state.selected_channel.id !==
				this.props.edit_data.sent_channel_data.id
		) {
			const original_channel_name =
				this.props.all_text_channels.find(
					(val) =>
						val.id === this.props.edit_data.sent_channel_data.id
				)?.name ?? "unknown";

			below_element = (
				<div className="note mt-4 w-full truncate whitespace-break-spaces">
					Event will be moved from{" "}
					<span className="has-text-weight-bold">
						#{original_channel_name}
					</span>{" "}
					to{" "}
					<span className="has-text-weight-bold">
						#{this.getSelectedChannel().name}
					</span>
				</div>
			);
		}

		return (
			<ChannelSelectFormWithTags
				id="selected_channel"
				key="selected_channel"
				sesh_channel_id={this.props.event_channel_id}
				name="Channel"
				desired_channel_id={
					this.props.is_edit ? undefined : this.props.event_channel_id
				}
				below_element={below_element}
				initial_value={this.getSelectedChannel()}
				channel_data={this.props.all_text_channels}
				info={channel_select_info}
				disabled={force_disable || channel_select_disabled}
				is_event
				updateFunc={(val) => {
					for (const key of Object.keys(GuildSettingToEventSetting)) {
						const guild_setting_key =
							key as keyof typeof GuildSettingToEventSetting;

						if (this.getModifiedSettingsKey(guild_setting_key)) {
							continue;
						}

						const state_key =
							GuildSettingToEventSetting[guild_setting_key];

						const new_state = {
							[state_key]:
								this.getGuildOrChannelSettingWithChannelParam(
									guild_setting_key,
									val
								),
						} as any;

						this.setState(new_state);
					}

					this.setState({
						selected_channel: {
							id: val.id,
							forum_tag_ids: [],
						},
					});
				}}
				include_threads={true}
				selected_forum_tag_ids={
					this.state.selected_channel.forum_tag_ids
				}
				setSelectedTagIds={(val) =>
					this.setState({
						selected_channel: {
							...this.state.selected_channel,
							forum_tag_ids: val,
						},
					})
				}
			></ChannelSelectFormWithTags>
		);
	}

	createDescriptionField(force_disable: boolean = false) {
		return (
			<div key="description" className="field">
				{DashboardUtils.CreateHeaderAndInfoButton(
					"Description",
					"The description of the event",
					false,
					false,
					false,
					undefined,
					force_disable
				)}
				<TextAreaForm
					initial_value={this.state.description}
					rows={10}
					placeholder={this.state.placeholder_fields.description}
					updateFunc={(val) => this.updateState("description", val)}
					char_limit={Constants.DESCRIPTION_CHAR_LIMIT}
					do_not_clear_on_unmount
				></TextAreaForm>
			</div>
		);
	}

	createImageField(force_disable: boolean = false) {
		return (
			<div className="field active" key="image_url_parent">
				<ImageUploaderForm
					key="image_url"
					initial_value={this.state.image_url}
					updateFunc={(val) => this.updateState("image_url", val)}
					disabled={force_disable}
					name="Image"
				/>
			</div>
		);
	}

	createThreadField(force_disable: boolean = false) {
		let error: JSX.Element;

		const selected_channel = this.getSelectedChannel();

		if (!this.state.loading_roles_and_channels) {
			if (
				selected_channel?.type === "Thread" ||
				selected_channel?.type === "Forum"
			) {
				error = (
					<>
						Can not use this feature when selected channel is a
						thread or forum channel
					</>
				);
			} else if (!selected_channel?.can_make_public_threads) {
				error = (
					<>
						sesh requires the{" "}
						<span className="has-text-weight-bold">
							Create Public Threads
						</span>{" "}
						permission in channel{" "}
						<span className="has-text-weight-bold">
							#
							{DashboardUtils.LimitName(
								selected_channel?.name,
								20
							)}
						</span>{" "}
						to use this feature
					</>
				);
			}
		}

		return (
			<EventThreadAccordian
				type="EventCreation"
				key={`event_thread_accordian_${selected_channel?.id}`}
				event_name={this.state.event_name}
				start_time={this.state.start_time?.toJSDate()}
				guild_timezone={this.props.guild_timezone}
				value={this.state.thread_settings}
				revert_descriptor={this.getRevertDescriptor(
					"default_event_thread_settings"
				)}
				onUpdate={(val) =>
					this.setState({
						thread_settings: val,
					})
				}
				disabled={force_disable}
				is_loading={this.state.loading_roles_and_channels}
				error={error}
				show_on_closed
			/>
		);
	}

	createColorField(force_disable: boolean = false) {
		return (
			<EventColorPicker
				key="color"
				label="Color"
				field_key={"color"}
				info={
					"The color of all embeds for this event, including notifications"
				}
				color={this.state.color}
				disabled={force_disable}
				updateFunc={(val) => this.updateState("color", val)}
				revert_descriptor={this.getRevertDescriptor(
					"default_event_embed_color"
				)}
			/>
		);
	}

	createDiscordNativeEventMirrorField(force_disable = false) {
		return (
			<DiscordNativeEventForm
				key={`native_event_data_${this.state.selected_channel?.id}`}
				is_disabled={force_disable}
				voice_channels={this.props.all_voice_channels}
				initial_event_data={this.state.native_event_data}
				updateFunc={(val) => this.updateState("native_event_data", val)}
				is_loading={this.state.loading_roles_and_channels}
				sesh_has_manage_events={this.props.sesh_has_manage_events}
				user_has_manage_events={this.props.user_has_manage_events}
				label="Mirror to Discord Native Event"
				revert_descriptor={this.getRevertDescriptor(
					"default_native_event_data"
				)}
				show_on_closed
				rsvp_options={this.state.rsvp_options.custom_options}
			/>
		);
	}

	createHideEventAttendeesField(force_disable: boolean = false) {
		return (
			<SeshAccordianComponent
				key="hide_attendees"
				label="Hide Attendee Names on Embed"
				is_opened={this.state.hide_attendees}
				info="Whether to show individual names of those attending in the event embed (Note: Names will still be visible via the web interface)"
				disabled={force_disable}
				revert_descriptor={this.getRevertDescriptor(
					"default_hide_attendees"
				)}
				onChange={(value) => this.updateState("hide_attendees", value)}
			></SeshAccordianComponent>
		);
	}

	getGuildOrChannelSetting<
		Key extends keyof EventCreationProps["guild_defaults"]
	>(key: Key): EventCreationProps["guild_defaults"][Key] {
		if (!this.props.is_premium) {
			return this.props.guild_defaults[key];
		}

		return (
			guildSettingsStyle_getIndividualChannelSettingOverride(
				this.props.guild_defaults,
				this.getSelectedChannel()?.overrides,
				key,
				{
					all_text_channels: this.props.all_text_channels,
					guild_data: { id: this.props.guild_id },
					roles: this.props.roles,
				}
			) ??
			(DefaultGuildSettingValues[
				key
			] as EventCreationProps["guild_defaults"][Key])
		);
	}

	getRevertDescriptor<Key extends keyof GuildDefaultsWithoutTimezone>(
		key: Key
	): RevertDescriptor<GuildDefaultsWithoutTimezone[Key]> | undefined {
		if (this.props.is_edit) {
			return undefined;
		}

		const event_key = GuildSettingToEventSetting[key];

		const guild_value =
			this.props.guild_defaults[key] ??
			(DefaultGuildSettingValues[
				key
			] as EventCreationProps["guild_defaults"][Key]);

		const channel_value = this.getGuildOrChannelSettingWithChannelParam(
			key,
			this.getSelectedChannel()
		);

		const has_active_channel_override =
			guildSettingsStyle_hasActiveChannelOverrideForSetting(
				this.getSelectedChannel()?.overrides,
				key
			);

		const default_value = has_active_channel_override
			? channel_value
			: guild_value;

		return {
			value: this.state[event_key as string],
			default: {
				value: default_value,
				from: has_active_channel_override ? "Channel" : "Guild",
			},
			onRevertPressed: () => {
				this.updateModifiedSettingsKey(key, false);
				this.pending_revert_state_updates[event_key] = true;
				this.updateState(event_key, default_value as any);
			},
		};
	}

	getGuildOrChannelSettingWithChannelParam<
		Key extends keyof GuildDefaultsWithoutTimezone
	>(
		key: Key,
		channel: SeshTextChannelData<WebChannelTypes>
	): GuildDefaultsWithoutTimezone[Key] {
		const default_value = DefaultGuildSettingValues[
			key as Key
		] as GuildDefaultsWithoutTimezone[Key];

		if (!this.props.is_premium) {
			return this.props.guild_defaults[key] ?? default_value;
		}

		return (
			guildSettingsStyle_getIndividualChannelSettingOverride(
				this.props.guild_defaults,
				channel?.overrides,
				key,
				{
					all_text_channels: this.props.all_text_channels,
					guild_data: { id: this.props.guild_id },
					roles: this.props.roles,
				}
			) ?? default_value
		);
	}

	createAddRoleToAttendeesField(force_disable: boolean = false) {
		const disabled_due_to_no_roles_to_add =
			this.props.roles?.filter((val) => val.can_user_add_role).length ===
			0;

		const disabled = force_disable;

		return (
			<AddRoleToAttendeesEventForm
				key="attendee_role_data"
				sesh_has_manage_roles={this.props.sesh_has_manage_roles}
				sesh_role_name={this.props.sesh_role_name}
				setValue={(val) =>
					this.setState({
						attendee_role_data: val,
					})
				}
				is_server_premium={this.props.is_premium}
				value={this.state.attendee_role_data}
				disabled={disabled}
				has_no_roles_to_add={disabled_due_to_no_roles_to_add}
				event_duration_ms={this.state.duration_ms}
				revert_descriptor={this.getRevertDescriptor(
					"attendee_role_data"
				)}
			/>
		);
	}

	createRSVPOptionsField(force_disable: boolean = false) {
		return (
			<SignupOptionsBlurb
				key="rsvp_options"
				current_signup_preset={this.state.rsvp_options}
				setPreset={(val) =>
					this.setState({
						rsvp_options: val,
					})
				}
				user_presets={this.state.user_presets}
				custom_emojis={this.state.custom_emojis}
				is_disabled={force_disable}
				is_edit={this.props.is_edit}
				revert_descriptor={this.getRevertDescriptor("signup_options")}
			/>
		);
	}

	createRoleRestrictionsField(force_disable: boolean = false) {
		const disabled = force_disable || !this.props.is_premium;

		return (
			<div
				key="elgible_role_ids"
				className={classNames("field", {
					active: this.props.is_premium,
				})}
			>
				{DashboardUtils.CreateHeaderAndInfoButton(
					"Role Restrictions",
					"Limit event signup to only certain roles",
					!this.props.is_premium,
					false,
					false,
					undefined,
					disabled
				)}
				<RoleRestrictionsForm
					role_ids={this.state.eligible_role_ids}
					updateFunc={(val) => {
						this.updateState("eligible_role_ids", val);
					}}
					disabled={disabled}
				></RoleRestrictionsForm>
			</div>
		);
	}

	createLockRSVPsBeforeField(force_disable: boolean = false) {
		return (
			<LockRSVPsBefore key="lock_rsvps_before" disabled={force_disable} />
		);
	}

	createRepeatField(force_disable: boolean = false) {
		return (
			<RepeatFormComponent
				key="rrule"
				end_time={this.getCalculatedEndTimeIfSet()}
				disabled={force_disable}
			/>
		);
	}

	renderFields() {
		let primary_fields: JSX.Element[] = [];

		const should_force_disable = this.state.is_confirming;

		for (let field_key in FIELD_ORDER) {
			primary_fields.push(
				this.fields[field_key.toString()](should_force_disable)
			);
		}

		return (
			<span>
				<div className="creation-primary-fields">
					{this.props.edit_data?.rrule_str && (
						<div className="field !-mb-4">
							<Note>
								You are currently editing a repeat event. Any
								edits made here will affect all current and
								future events.
							</Note>
						</div>
					)}
					{!this.props.is_edit && (
						<div className="field !p-0 !pr-2 !pt-1">
							<EventTemplateDropdown />
						</div>
					)}
					{primary_fields}
				</div>
				<EventCreationAdvancedSelector
					field_key_to_element={this.fields}
					is_disabled={should_force_disable}
				/>
				{/* <div className="creation-form">{secondary_fields}</div> */}
			</span>
		);
	}

	renderAccordians() {
		return (
			<div className="creation-form">
				<h1 className="title is-3 has-text-centered mt-6">
					{this.state.header_text}
				</h1>
				{this.props.is_edit && (
					<span
						className="rsvp-data-link-edit-link"
						onClick={() => {
							this.props.history.push(
								common_utils.getAttendeesDisplayURL(
									this.props.guild_id,
									this.props.edit_data?.event_message_id,
									true
								)
							);
						}}
					>
						View RSVPs
					</span>
				)}
				{this.renderFields()}
				<div className="field submit-button flex flex-col gap-4">
					{this.props.is_edit && (
						<div>
							<input
								type="checkbox"
								name="has_reminders"
								checked={this.state.inform_users_of_edit}
								onChange={(e) =>
									this.setState({
										inform_users_of_edit: e.target.checked,
									})
								}
							/>{" "}
							Inform RSVPed users of changes via DM
						</div>
					)}
					<SaveCurrentSettingsButton
						is_premium={this.props.is_premium}
					/>
					<DashboardUtils.CreateFormConfirmButton
						blocked_by_load={this.state.loading_roles_and_channels}
						channel_name={this.getSelectedChannel()?.name}
						is_confirming={this.state.is_confirming}
						onClick={this.handleSubmit}
						is_edit={this.props.is_edit}
						is_event={true}
					/>
				</div>
			</div>
		);
	}

	renderLinkChains() {
		const link_chain_data: LinkChainData[] = [];

		link_chain_data.push(DashboardUtils.GetGuildSelectionLinkChainData());
		link_chain_data.push(
			DashboardUtils.RenderGuildOptionSelectLinkChain(
				this.state.guild_data_for_this_guild
			)
		);

		link_chain_data.push(
			DashboardUtils.RenderEventSelectionLinkChain(
				this.state.guild_data_for_this_guild
			)
		);

		link_chain_data.push(
			DashboardUtils.RenderEventCreateLinkChain(
				this.props.guild_id,
				this.props.is_edit
			)
		);

		return (
			<DashboardUtils.RenderLinkChain link_chain_data={link_chain_data} />
		);
	}

	render() {
		const providerValue: ComponentProps<
			typeof EventCreationContext.Provider
		>["value"] = {
			state: this.state,
			setState: (val: Parameters<typeof this.setState>[0]) =>
				this.setState(val),
			props: this.props,
		};

		if (this.state.current_page === "templates") {
			return (
				<EventCreationContext.Provider value={providerValue}>
					<EventTemplatePage
						is_guild_premium={this.props.is_premium}
						onBack={() => {
							this.setState({
								current_page: "main",
							});
						}}
					/>
				</EventCreationContext.Provider>
			);
		}

		return (
			<EventCreationContext.Provider value={providerValue}>
				<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
					<div
						className="columns is-mobile is-centered !m-0"
						style={{ transition: "opacity 0.5s", opacity: "1" }}
					>
						<div className="column is-11-mobile is-three-quarters-tablet is-three-quarters-desktop is-half-widescreen is-two-fifths-fullhd create-event-container">
							{this.renderLinkChains()}
							{this.renderAccordians()}
						</div>
					</div>
				</FadeIn>
			</EventCreationContext.Provider>
		);
	}
}

function sendEditData(request_body: ISendEditEventMessage) {
	return fetch(`/api/edit_event`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	});
}

async function sendEventData(request_body: ISendCreateEventMessage) {
	let result = fetch(`/api/create_event`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	});

	return result;
}

export default withRouter(EventCreation);
